import { useState, useRef } from "react";
import { useParams, Link } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Badge } from "@nosible/shared/components/ui/badge";
import { Button } from "@nosible/shared/components/ui/button";
import { Input } from "@nosible/shared/components/ui/input";
import { Label } from "@nosible/shared/components/ui/label";
import { Switch } from "@nosible/shared/components/ui/switch";
import { Textarea } from "@nosible/shared/components/ui/textarea";
import { trpc } from "@/admin/lib/trpc";
import { CreditCard, ArrowLeft, Users, Key, Pencil, X, Save, Loader2, Check, Plus, Trash2, FileText, Tag, GripVertical } from "lucide-react";
import { RateLimits } from "@/admin/components/RateLimits";
import { PlanSubscriptions } from "@/admin/components/PlanSubscriptions";

// ---------------------------------------------------------------------------
// PlanName component
// ---------------------------------------------------------------------------
function PlanName({
  planId,
  name,
  onSaved,
}: {
  planId: string;
  name: string;
  onSaved: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(name);

  const mutation = trpc.admin.updatePlanName.useMutation({
    onSuccess: () => {
      onSaved();
      setIsEditing(false);
    },
  });

  const startEditing = () => {
    setValue(name);
    setIsEditing(true);
  };

  const cancel = () => {
    setValue(name);
    setIsEditing(false);
  };

  const save = () => {
    if (value.trim() && value.trim() !== name) {
      mutation.mutate({ planId, name: value.trim() });
    } else {
      setIsEditing(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Plan Name
            </CardTitle>
            <CardDescription>The display name of this plan</CardDescription>
          </div>
          {!isEditing && (
            <Button variant="outline" size="sm" onClick={startEditing}>
              <Pencil className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="plan-name">Name</Label>
              <Input
                id="plan-name"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="bg-obsidian border-slate-core"
                placeholder="Plan name"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={cancel} disabled={mutation.isPending}>
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button onClick={save} disabled={mutation.isPending || !value.trim()}>
                {mutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Save
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-silica font-medium">{name}</p>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PlanDesc component
// ---------------------------------------------------------------------------
function PlanDesc({
  planId,
  description,
  onSaved,
}: {
  planId: string;
  description: string | null;
  onSaved: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(description ?? "");

  const mutation = trpc.admin.updatePlanDescription.useMutation({
    onSuccess: () => {
      onSaved();
      setIsEditing(false);
    },
  });

  const startEditing = () => {
    setValue(description ?? "");
    setIsEditing(true);
  };

  const cancel = () => {
    setValue(description ?? "");
    setIsEditing(false);
  };

  const save = () => {
    mutation.mutate({ planId, description: value.trim() || null });
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Description
            </CardTitle>
            <CardDescription>A short description shown to users</CardDescription>
          </div>
          {!isEditing && (
            <Button variant="outline" size="sm" onClick={startEditing}>
              <Pencil className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="plan-description">Description</Label>
              <Textarea
                id="plan-description"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="bg-obsidian border-slate-core min-h-[80px]"
                placeholder="Enter a description for this plan…"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={cancel} disabled={mutation.isPending}>
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button onClick={save} disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Save
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-silica">
            {description ?? <span className="text-chromium-mist italic">No description set</span>}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PlanKeyFeatures component
// ---------------------------------------------------------------------------
function PlanKeyFeatures({
  planId,
  keyFeatures,
  onSaved,
}: {
  planId: string;
  keyFeatures: string[];
  onSaved: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [items, setItems] = useState<string[]>(keyFeatures);
  const [newItem, setNewItem] = useState("");
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const dragIdx = useRef<number | null>(null);

  const mutation = trpc.admin.updatePlanKeyFeatures.useMutation({
    onSuccess: () => {
      onSaved();
      setIsEditing(false);
    },
  });

  const startEditing = () => {
    setItems([...keyFeatures]);
    setNewItem("");
    setIsEditing(true);
  };

  const cancel = () => {
    setItems([...keyFeatures]);
    setNewItem("");
    setIsEditing(false);
  };

  const addItem = () => {
    const trimmed = newItem.trim();
    if (trimmed) {
      setItems((prev) => [...prev, trimmed]);
      setNewItem("");
    }
  };

  const removeItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const onDragStart = (idx: number) => {
    dragIdx.current = idx;
  };

  const onDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIdx(idx);
  };

  const onDrop = (idx: number) => {
    if (dragIdx.current === null || dragIdx.current === idx) {
      setDragOverIdx(null);
      return;
    }
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx.current!, 1);
      next.splice(idx, 0, moved!);
      return next;
    });
    dragIdx.current = null;
    setDragOverIdx(null);
  };

  const onDragEnd = () => {
    dragIdx.current = null;
    setDragOverIdx(null);
  };

  const save = () => {
    mutation.mutate({ planId, keyFeatures: items.filter((f) => f.trim()) });
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Check className="h-5 w-5" />
              Key Features
            </CardTitle>
            <CardDescription>Highlighted features and capabilities of this plan</CardDescription>
          </div>
          {!isEditing && (
            <Button variant="outline" size="sm" onClick={startEditing}>
              <Pencil className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <ul className="space-y-2">
              {items.map((feature, idx) => (
                <li
                  key={idx}
                  draggable
                  onDragStart={() => onDragStart(idx)}
                  onDragOver={(e) => onDragOver(e, idx)}
                  onDrop={() => onDrop(idx)}
                  onDragEnd={onDragEnd}
                  className={`flex items-center gap-2 rounded px-1 transition-colors ${dragOverIdx === idx ? "bg-slate-core" : ""
                    }`}
                >
                  <GripVertical className="h-4 w-4 text-chromium-mist shrink-0 cursor-grab active:cursor-grabbing" />
                  <Check className="h-4 w-4 text-neon-green shrink-0" />
                  <span className="flex-1 text-sm text-silica">{feature}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeItem(idx)}
                    className="h-7 w-7 p-0 text-chromium-mist hover:text-red-400"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              <Input
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addItem()}
                placeholder="Add a feature…"
                className="bg-obsidian border-slate-core"
              />
              <Button variant="outline" onClick={addItem} disabled={!newItem.trim()}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-core">
              <Button variant="ghost" onClick={cancel} disabled={mutation.isPending}>
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button onClick={save} disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Save
              </Button>
            </div>
          </div>
        ) : keyFeatures.length > 0 ? (
          <ul className="space-y-2">
            {keyFeatures.map((feature, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-neon-green shrink-0" />
                <span className="text-silica">{feature}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-chromium-mist italic text-sm">No key features defined</p>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PlanPrice component
// ---------------------------------------------------------------------------
function PlanPrice({
  planId,
  monthlyPriceCents,
  onSaved,
}: {
  planId: string;
  monthlyPriceCents: number;
  onSaved: () => void;
}) {
  const isCustom = monthlyPriceCents === -1;
  const [isEditing, setIsEditing] = useState(false);
  const [isCustomPrice, setIsCustomPrice] = useState(isCustom);
  const [dollars, setDollars] = useState(
    isCustom ? "" : (monthlyPriceCents / 100).toFixed(2)
  );

  const mutation = trpc.admin.updatePlanPrice.useMutation({
    onSuccess: () => {
      onSaved();
      setIsEditing(false);
    },
  });

  const displayPrice = (cents: number) => {
    if (cents === -1) return "Custom";
    if (cents === 0) return "Free";
    return `$${(cents / 100).toFixed(2)}/mo`;
  };

  const startEditing = () => {
    setIsCustomPrice(monthlyPriceCents === -1);
    setDollars(monthlyPriceCents === -1 ? "" : (monthlyPriceCents / 100).toFixed(2));
    setIsEditing(true);
  };

  const cancel = () => setIsEditing(false);

  const save = () => {
    if (isCustomPrice) {
      mutation.mutate({ planId, monthlyPriceCents: null });
    } else {
      const parsed = parseFloat(dollars);
      if (!isNaN(parsed) && parsed >= 0) {
        mutation.mutate({ planId, monthlyPriceCents: Math.round(parsed * 100) });
      }
    }
  };

  const isValid = isCustomPrice || (!isNaN(parseFloat(dollars)) && parseFloat(dollars) >= 0);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardDescription>Price</CardDescription>
            {!isEditing && (
              <CardTitle className="text-2xl">{displayPrice(monthlyPriceCents)}</CardTitle>
            )}
          </div>
          {!isEditing && (
            <Button variant="ghost" size="sm" onClick={startEditing} className="h-7 w-7 p-0 text-chromium-mist">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Switch
                id="custom-price"
                checked={isCustomPrice}
                onCheckedChange={(v) => {
                  setIsCustomPrice(v);
                  if (v) setDollars("");
                }}
              />
              <Label htmlFor="custom-price" className="text-sm text-chromium-mist cursor-pointer">
                Custom
              </Label>
            </div>
            {!isCustomPrice && (
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-chromium-mist text-sm">$</span>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={dollars}
                  onChange={(e) => setDollars(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && isValid && save()}
                  className="bg-obsidian border-slate-core h-8 pl-6 text-lg font-bold"
                  placeholder="0.00"
                  autoFocus
                />
              </div>
            )}
            <div className="flex justify-end gap-1">
              <Button variant="ghost" size="sm" onClick={cancel} disabled={mutation.isPending}>
                <X className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" onClick={save} disabled={mutation.isPending || !isValid}>
                {mutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-xs text-chromium-mist/70 italic">
            Display only — not connected to Stripe
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PlanApiKeyLimit component
// ---------------------------------------------------------------------------
function PlanApiKeyLimit({
  planId,
  defaultApiKeyLimit,
  onSaved,
}: {
  planId: string;
  defaultApiKeyLimit: number;
  onSaved: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(String(defaultApiKeyLimit));

  const mutation = trpc.admin.updatePlanApiKeyLimit.useMutation({
    onSuccess: () => {
      onSaved();
      setIsEditing(false);
    },
  });

  const startEditing = () => {
    setValue(String(defaultApiKeyLimit));
    setIsEditing(true);
  };

  const cancel = () => {
    setValue(String(defaultApiKeyLimit));
    setIsEditing(false);
  };

  const save = () => {
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      mutation.mutate({ planId, defaultApiKeyLimit: parsed });
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardDescription>API Key Limit</CardDescription>
            {!isEditing && (
              <CardTitle className="text-2xl">{defaultApiKeyLimit}</CardTitle>
            )}
          </div>
          {!isEditing ? (
            <Button variant="ghost" size="sm" onClick={startEditing} className="h-7 w-7 p-0 text-chromium-mist">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-2">
            <Input
              type="number"
              min={0}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && save()}
              className="bg-obsidian border-slate-core h-8 text-lg font-bold"
              autoFocus
            />
            <div className="flex justify-end gap-1">
              <Button variant="ghost" size="sm" onClick={cancel} disabled={mutation.isPending}>
                <X className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" onClick={save} disabled={mutation.isPending || isNaN(parseInt(value, 10))}>
                {mutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-1 text-chromium-mist text-sm">
            <Key className="h-4 w-4" />
            keys per subscription
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PlanStripeConfig component
// ---------------------------------------------------------------------------
function PlanStripeConfig({
  planId,
  stripeManaged,
  stripePriceId,
  stripeAnnualPriceId,
  stripeProductId,
  onSaved,
}: {
  planId: string;
  stripeManaged: boolean;
  stripePriceId: string | null;
  stripeAnnualPriceId: string | null;
  stripeProductId: string | null;
  onSaved: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editStripeManaged, setEditStripeManaged] = useState(stripeManaged);
  const [editStripePriceId, setEditStripePriceId] = useState(stripePriceId ?? "");
  const [editStripeAnnualPriceId, setEditStripeAnnualPriceId] = useState(stripeAnnualPriceId ?? "");
  const [editStripeProductId, setEditStripeProductId] = useState(stripeProductId ?? "");

  const mutation = trpc.admin.updatePlan.useMutation({
    onSuccess: () => {
      onSaved();
      setIsEditing(false);
    },
  });

  const startEditing = () => {
    setEditStripeManaged(stripeManaged);
    setEditStripePriceId(stripePriceId ?? "");
    setEditStripeAnnualPriceId(stripeAnnualPriceId ?? "");
    setEditStripeProductId(stripeProductId ?? "");
    setIsEditing(true);
  };

  const cancel = () => setIsEditing(false);

  const save = () => {
    mutation.mutate({
      planId,
      stripeManaged: editStripeManaged,
      stripePriceId: editStripeManaged ? (editStripePriceId || null) : null,
      stripeAnnualPriceId: editStripeManaged ? (editStripeAnnualPriceId || null) : null,
      stripeProductId: editStripeManaged ? (editStripeProductId || null) : null,
    });
  };

  return isEditing ? (
    <Card className="mb-6 border-violet-pulse">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Stripe Configuration
        </CardTitle>
        <CardDescription>Configure whether this plan is managed by Stripe</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="stripe-managed">Stripe Managed</Label>
            <p className="text-sm text-chromium-mist">
              Enable to link this plan to Stripe products and prices
            </p>
          </div>
          <Switch
            id="stripe-managed"
            checked={editStripeManaged}
            onCheckedChange={setEditStripeManaged}
          />
        </div>

        {editStripeManaged && (
          <div className="space-y-4 pt-4 border-t border-slate-core">
            <div className="space-y-2">
              <Label htmlFor="stripe-product-id">Stripe Product ID</Label>
              <Input
                id="stripe-product-id"
                placeholder="prod_xxx"
                value={editStripeProductId}
                onChange={(e) => setEditStripeProductId(e.target.value)}
                className="bg-obsidian border-slate-core"
              />
              <p className="text-xs text-chromium-mist">The Stripe Product ID (e.g., prod_xxx)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="stripe-price-id">Monthly Price ID</Label>
              <Input
                id="stripe-price-id"
                placeholder="price_xxx"
                value={editStripePriceId}
                onChange={(e) => setEditStripePriceId(e.target.value)}
                className="bg-obsidian border-slate-core"
              />
              <p className="text-xs text-chromium-mist">The Stripe Price ID for monthly billing</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="stripe-annual-price-id">Annual Price ID (Optional)</Label>
              <Input
                id="stripe-annual-price-id"
                placeholder="price_xxx"
                value={editStripeAnnualPriceId}
                onChange={(e) => setEditStripeAnnualPriceId(e.target.value)}
                className="bg-obsidian border-slate-core"
              />
              <p className="text-xs text-chromium-mist">The Stripe Price ID for annual billing (optional)</p>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t border-slate-core">
          <Button variant="ghost" onClick={cancel} disabled={mutation.isPending}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button onClick={save} disabled={mutation.isPending}>
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Save Changes
          </Button>
        </div>
      </CardContent>
    </Card>
  ) : (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Stripe Configuration
            </CardTitle>
            <CardDescription>
              {stripeManaged ? "This plan is linked to Stripe" : "This plan is not linked to Stripe"}
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={startEditing}>
            <Pencil className="h-4 w-4 mr-1" />
            Edit Stripe Config
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {stripeManaged ? (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-chromium-mist">Product ID:</span>
              <span className="text-silica font-mono">{stripeProductId || "—"}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-chromium-mist">Monthly Price ID:</span>
              <span className="text-silica font-mono">{stripePriceId || "—"}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-chromium-mist">Annual Price ID:</span>
              <span className="text-silica font-mono">{stripeAnnualPriceId || "—"}</span>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-chromium-mist">
            <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>This plan is not linked to Stripe</p>
            <p className="text-sm">Click "Edit Stripe Config" to link it</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AdminPlan page
// ---------------------------------------------------------------------------
export function AdminPlan() {
  const { planId } = useParams<{ planId: string }>();

  const {
    data: planData,
    isLoading,
    error,
    refetch,
  } = trpc.admin.getPlan.useQuery(
    { planId: planId! },
    {
      enabled: !!planId,
    }
  );

  const updatePlanQueryLimitsMutation = trpc.admin.updatePlanQueryLimits.useMutation();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-red-400">Error loading plan: {error.message}</div>
      </div>
    );
  }

  if (!planData) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-red-400">Plan not found</div>
      </div>
    );
  }

  const { plan, subscriptions } = planData;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/plans">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Plans
          </Link>
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <CreditCard className="h-6 w-6 text-violet-pulse" />
        <h1 className="text-2xl font-bold text-silica font-display">{plan.name}</h1>
        {plan.active ? (
          <Badge variant="outline" className="text-neon-green border-neon-green">
            Active
          </Badge>
        ) : (
          <Badge variant="secondary">Inactive</Badge>
        )}
      </div>

      {/* Name & Description */}
      <PlanName planId={plan.id} name={plan.name} onSaved={refetch} />
      <PlanDesc planId={plan.id} description={plan.description} onSaved={refetch} />

      {/* Plan Details */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
        <PlanPrice
          planId={plan.id}
          monthlyPriceCents={plan.monthlyPriceCents}
          onSaved={refetch}
        />

        <PlanApiKeyLimit
          planId={plan.id}
          defaultApiKeyLimit={plan.defaultApiKeyLimit}
          onSaved={refetch}
        />

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Status</CardDescription>
            <CardTitle className="text-2xl">{plan.active ? "Active" : "Inactive"}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-1 text-chromium-mist text-sm">
              {plan.active ? (
                <Badge variant="outline" className="text-neon-green border-neon-green">Available</Badge>
              ) : (
                <Badge variant="secondary">Legacy</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Key Features */}
      <PlanKeyFeatures
        planId={plan.id}
        keyFeatures={plan.keyFeatures ?? []}
        onSaved={refetch}
      />

      {/* Stripe Configuration */}
      <PlanStripeConfig
        planId={plan.id}
        stripeManaged={plan.stripeManaged}
        stripePriceId={plan.stripePriceId}
        stripeAnnualPriceId={plan.stripeAnnualPriceId}
        stripeProductId={plan.stripeProductId}
        onSaved={refetch}
      />

      {/* Rate Limits */}
      <div className="mb-6">
        <RateLimits
          queryLimits={plan.defaultQueryLimits || []}
          onSave={async (limits) => {
            await updatePlanQueryLimitsMutation.mutateAsync({
              planId: plan.id,
              queryLimits: limits,
            });
            await refetch();
          }}
          isLoading={updatePlanQueryLimitsMutation.isPending}
        />
      </div>

      {/* Subscriptions */}
      <PlanSubscriptions planId={plan.id} />
    </div>
  );
}
